/**
 * Whether this browser can put the app on the home screen, and how.
 *
 * A module-level store read through `useSyncExternalStore`, for the same reason the
 * offline queue is one: the thing being observed is the browser, not React. In
 * particular `beforeinstallprompt` fires once, early — Chrome dispatches it during page
 * load, often before the first render — and it cannot be asked for again. A listener
 * attached inside a component effect is attached too late to catch it, so `startInstallOffer`
 * is called from `main.tsx` before the root renders and holds the event until someone
 * asks for it.
 *
 * The two install paths are genuinely different and neither can be emulated by the other:
 * Chrome hands us an event to fire; Safari has no API at all and the person has to be
 * told where the button is.
 */

/** Not in the DOM lib — it is a Chromium extension to the spec. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/**
 * `prompt` — the browser will show its own install dialog when we ask it to.
 * `ios-safari` — installing is possible but only by hand, through the Share sheet.
 */
export type InstallMethod = 'prompt' | 'ios-safari';

export interface InstallSnapshot {
  /**
   * What to offer unasked, on the home screen. Null when the app is installed, when the
   * offer was turned down, or when it cannot be installed here.
   */
  offer: InstallMethod | null;

  /**
   * How the app could be put on the home screen at all, whether or not anyone has been
   * offered it. This is the one Profile reads: somebody who has gone looking for the
   * button is asking now, and a "not now" tapped on the banner weeks ago — or on a phone
   * they have since handed to someone else — must not answer on their behalf.
   */
  capability: InstallMethod | null;
}

const DISMISSED_KEY = 'bnb.installDismissed';

type Listener = () => void;

const listeners = new Set<Listener>();

let snapshot: InstallSnapshot = { offer: null, capability: null };
let deferredPrompt: BeforeInstallPromptEvent | null = null;
let started = false;

function emit(next: InstallSnapshot): void {
  // useSyncExternalStore compares by identity, so only publish real changes.
  if (next.offer === snapshot.offer && next.capability === snapshot.capability) {
    return;
  }

  snapshot = next;

  for (const listener of listeners) {
    listener();
  }
}

export function subscribeToInstallOffer(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getInstallSnapshot(): InstallSnapshot {
  return snapshot;
}

/**
 * Already running from the home screen? Then there is nothing to offer.
 *
 * `display-mode` covers Android and desktop; `navigator.standalone` is Safari's older
 * non-standard equivalent, and iOS still reports the app that way.
 */
function isInstalled(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const standalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;

  return standalone || window.matchMedia('(display-mode: standalone)').matches;
}

function isDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) !== null;
  } catch {
    // Private mode with storage denied. Offering the banner is the safe answer: it is
    // dismissible, and the alternative is never offering it at all.
    return false;
  }
}

/**
 * iOS, in the one browser that can actually do it.
 *
 * Every browser on iOS renders with WebKit, but only Safari's share sheet carries
 * "Add to Home Screen" — telling a Chrome-for-iOS user to look for it sends them hunting
 * for something that is not there. iPadOS 13+ claims to be a Mac in its user agent, so
 * the touch points are what give an iPad away.
 */
function isIosSafari(): boolean {
  const agent = navigator.userAgent;
  const isIos =
    /iphone|ipad|ipod/i.test(agent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  if (!isIos) {
    return false;
  }

  return !/crios|fxios|edgios|opios|fban|fbav|instagram/i.test(agent);
}

/**
 * Starts watching for the chance to offer an install. Idempotent, so it does not matter
 * how many times it is called.
 */
export function startInstallOffer(): void {
  if (started || typeof window === 'undefined') {
    return;
  }

  started = true;

  window.addEventListener('beforeinstallprompt', (event) => {
    // Suppresses Chrome's own mini-infobar in favour of the banner, which sits in the
    // page where it can say what installing is *for* rather than just offering it.
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;

    // The capability is recorded even when the banner has been turned down: the event is
    // what Profile's button fires, and it is held for exactly as long as this page lives.
    emit({ capability: 'prompt', offer: isDismissed() ? null : 'prompt' });
  });

  // Fired after the install completes, including from the browser's own menu. Withdrawing
  // the offer at that point is the only signal we get on Android that it worked.
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    emit({ capability: null, offer: null });
  });

  if (isInstalled()) {
    return;
  }

  if (isIosSafari()) {
    emit({ capability: 'ios-safari', offer: isDismissed() ? null : 'ios-safari' });
  }
}

/**
 * Shows the browser's install dialog. Resolves once the person has answered it.
 *
 * The event is single-use and Chrome does not dispatch a second one for this page, so the
 * capability goes with the offer — there is genuinely nothing left to fire, and a Profile
 * button that did nothing would be worse than one that has gone. Turning the dialog down
 * is not recorded: Chrome dispatches `beforeinstallprompt` again on a later visit, and
 * letting it come back then is the whole reason it is not persisted here.
 */
export async function promptToInstall(): Promise<void> {
  const event = deferredPrompt;

  if (!event) {
    return;
  }

  deferredPrompt = null;
  emit({ capability: null, offer: null });

  await event.prompt();
  await event.userChoice;
}

/**
 * Stops offering, for good.
 *
 * Permanent on purpose. Somebody who said no to this once and is asked again every time
 * they open a shopping list will learn to swipe past the top of the screen, which is
 * where the invitations are.
 *
 * It stops the app *asking*; it does not stop it being possible. The capability survives,
 * which is what leaves the row in Profile for whoever changes their mind.
 */
export function dismissInstallOffer(): void {
  try {
    localStorage.setItem(DISMISSED_KEY, new Date().toISOString());
  } catch {
    // Storage denied: the banner will be back next launch. Nothing better is available.
  }

  emit({ capability: snapshot.capability, offer: null });
}

/** Test seam: resets module state between test cases. */
export function resetInstallOfferForTests(): void {
  snapshot = { offer: null, capability: null };
  deferredPrompt = null;
  started = false;
  listeners.clear();
}
